import { useEffect } from "react";
import {
  Box,
  Camera,
  Copy,
  Eraser,
  GitMerge,
  Grid3X3,
  MapPin,
  Plus,
  ScanSearch,
  Spline,
  Trash2,
  Zap,
} from "lucide-react";
import Tooltip from "../../components/ui/Tooltip";
import OrderedIconActionContextRow from "../../components/ui/OrderedIconActionContextRow";
import DuplicateMenuContent from "../../components/ui/DuplicateMenuContent";
import {
  BallJointTypeIcon,
  BoxGeomIcon,
  CapsuleGeomIcon,
  CylinderGeomIcon,
  EntireObjectIcon,
  EllipsoidIcon,
  ExplosionIcon,
  JointIcon,
  SlideJointTypeIcon,
  SphereGeomIcon,
} from "../../components/ui/Icons";
import {
  getJointTypeMenuOptions,
  NON_FREE_JOINT_TYPE_OPTIONS,
} from "../../utils/jointTypeValidation";

export function SceneContextMenu({
  activeSubmenu,
  closeSubmenuSoon,
  contextAddTargetBodyUuid,
  handleContextAddBody,
  handleContextAddCameraFromScenePick,
  handleContextAddConstraint,
  handleContextAddFixedWorldCamera,
  handleContextAddGeomToBody,
  handleContextAddJointToBody,
  handleContextAddSiteToBody,
  handleContextAddTendon,
  handleContextAddVisualGeomToBody,
  handleContextClearScene,
  handleContextDeleteSelect,
  handleContextDuplicateBody,
  handleContextDuplicateCollision,
  handleContextInspectSelect,
  hasContextTarget,
  isPhysicsRunning,
  openSubmenu,
  safeBodies,
  safeJointMapObject,
  sceneAddGeomKindSubmenuOpen,
  sceneAddJointKindSubmenuOpen,
  sceneAddVisualGeomKindSubmenuOpen,
  sceneContextMenuData,
  sceneContextMenuOpen,
  sceneContextMenuPos,
  sceneContextMenuRef,
  setActiveSubmenu,
  setSceneAddGeomKindSubmenuOpen,
  setSceneAddJointKindSubmenuOpen,
  setSceneAddVisualGeomKindSubmenuOpen,
  worldRootTargetBodyUuid,
}) {
  useEffect(() => {
    if (!sceneContextMenuOpen) return;
    const firstItem = sceneContextMenuRef.current?.querySelector(
      '[role="menuitem"]:not(:disabled)'
    );
    firstItem?.focus?.({ preventScroll: true });
  }, [sceneContextMenuOpen, sceneContextMenuRef]);

  const renderContextAddSubmenu = (targetBodyUuid, addBodyToRoot = false) => {
    const canAutoParentGeom = !!targetBodyUuid || addBodyToRoot;
    const canAutoParentJoint = canAutoParentGeom;
    const existingAutoTargetBodyUuid =
      !targetBodyUuid && addBodyToRoot ? worldRootTargetBodyUuid : null;
    const jointValidationBodyUuid = addBodyToRoot
      ? null
      : targetBodyUuid || existingAutoTargetBodyUuid;
    const jointKindOptions = addBodyToRoot
      ? NON_FREE_JOINT_TYPE_OPTIONS
      : jointValidationBodyUuid
        ? getJointTypeMenuOptions({
            bodies: safeBodies,
            joints: safeJointMapObject,
            bodyUuid: jointValidationBodyUuid,
            includeFree: false,
          })
        : NON_FREE_JOINT_TYPE_OPTIONS;
    const defaultJointKindForQuickAdd = "hinge";
    const defaultJointOption = jointKindOptions.find(
      (option) => option.kind === defaultJointKindForQuickAdd
    );
    const firstEnabledJointOption = jointKindOptions.find((option) => !option.disabled);
    const jointDisabledReason =
      canAutoParentJoint && !firstEnabledJointOption
        ? jointKindOptions.find((option) => option.description)?.description ||
          "No joint types can be added here."
        : "";
    const canAddAnyJoint = canAutoParentJoint && (addBodyToRoot || !!firstEnabledJointOption);
    const defaultJointDisabledReason = addBodyToRoot
      ? ""
      : defaultJointOption?.disabled
        ? defaultJointOption.description || "This joint type is not valid for this body."
        : "";
    const canQuickAddDefaultJoint = canAutoParentJoint && !defaultJointDisabledReason;
    const jointIconTooltip = jointDisabledReason
      ? jointDisabledReason
      : defaultJointDisabledReason
        ? `${defaultJointDisabledReason} Choose another joint type.`
        : "Joint";
    const candidateJointUuid = sceneContextMenuData.candidates.joint?.uuid || null;
    const candidateJoint = candidateJointUuid ? safeJointMapObject?.[candidateJointUuid] : null;
    const bodyForTendonUuid = targetBodyUuid || existingAutoTargetBodyUuid || null;
    const bodyTendonJointUuid = bodyForTendonUuid
      ? (safeBodies?.[bodyForTendonUuid]?.joints || []).find((jointUuid) => {
          const joint = safeJointMapObject?.[jointUuid];
          return joint?.kind === "hinge" || joint?.kind === "slide";
        }) || null
      : null;
    const fallbackTendonJointUuid =
      Object.values(safeJointMapObject || {}).find(
        (joint) => joint?.uuid && (joint.kind === "hinge" || joint.kind === "slide")
      )?.uuid || null;
    const tendonTargetJointUuid =
      candidateJoint?.kind === "hinge" || candidateJoint?.kind === "slide"
        ? candidateJointUuid
        : bodyTendonJointUuid || fallbackTendonJointUuid;
    const tendonTooltip = tendonTargetJointUuid
      ? "Tendon"
      : "Add a hinge or slide joint before adding a tendon";
    const jointIconByKind = {
      hinge: JointIcon,
      slide: SlideJointTypeIcon,
      ball: BallJointTypeIcon,
    };
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <Tooltip content="Body">
            <button
              type="button"
              role="menuitem"
              aria-label="Body"
              onClick={() => handleContextAddBody(addBodyToRoot ? null : targetBodyUuid)}
              onMouseEnter={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              onFocus={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
            >
              <Box className="w-4 h-4" aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip content="Visual">
            <button
              type="button"
              role="menuitem"
              aria-label="Visual"
              aria-haspopup="menu"
              aria-expanded={sceneAddVisualGeomKindSubmenuOpen}
              aria-disabled={!canAutoParentGeom}
              onClick={() => {
                if (!canAutoParentGeom) return;
                handleContextAddVisualGeomToBody(
                  addBodyToRoot ? null : (targetBodyUuid ?? null),
                  "box",
                  { forceNewRoot: addBodyToRoot }
                );
              }}
              onMouseEnter={() => {
                if (!canAutoParentGeom) return;
                setSceneAddVisualGeomKindSubmenuOpen(true);
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
              }}
              onFocus={() => {
                if (!canAutoParentGeom) return;
                setSceneAddVisualGeomKindSubmenuOpen(true);
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
              }}
              className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                canAutoParentGeom
                  ? "text-gray-200 hover:bg-gray-700/90"
                  : "text-gray-300 opacity-60 cursor-not-allowed"
              }`}
            >
              <Grid3X3 className="w-4 h-4" aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip content={jointIconTooltip}>
            <button
              type="button"
              role="menuitem"
              aria-label="Joint"
              aria-haspopup="menu"
              aria-expanded={sceneAddJointKindSubmenuOpen}
              aria-disabled={!canAddAnyJoint}
              onClick={() => {
                if (!canAddAnyJoint) return;
                if (!canQuickAddDefaultJoint) {
                  setSceneAddJointKindSubmenuOpen(true);
                  setSceneAddGeomKindSubmenuOpen(false);
                  setSceneAddVisualGeomKindSubmenuOpen(false);
                  return;
                }
                handleContextAddJointToBody(
                  addBodyToRoot ? null : (targetBodyUuid ?? null),
                  defaultJointKindForQuickAdd,
                  { forceNewRoot: addBodyToRoot }
                );
              }}
              onMouseEnter={() => {
                setSceneAddJointKindSubmenuOpen(canAddAnyJoint);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              onFocus={() => {
                setSceneAddJointKindSubmenuOpen(canAddAnyJoint);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                canAddAnyJoint
                  ? "text-gray-200 hover:bg-gray-700/90"
                  : "text-gray-300 opacity-60 cursor-not-allowed"
              }`}
            >
              <JointIcon className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip content="Site">
            <button
              type="button"
              role="menuitem"
              aria-label="Site"
              aria-disabled={!targetBodyUuid && !addBodyToRoot}
              onClick={() => {
                const parent = addBodyToRoot ? null : targetBodyUuid;
                if (!addBodyToRoot && !parent) return;
                handleContextAddSiteToBody(parent);
              }}
              onMouseEnter={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              onFocus={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                targetBodyUuid || addBodyToRoot
                  ? "text-gray-200 hover:bg-gray-700/90"
                  : "text-gray-300 opacity-60 cursor-not-allowed"
              }`}
            >
              <MapPin className="w-4 h-4" aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip content="Collision">
            <button
              type="button"
              role="menuitem"
              aria-label="Collision"
              aria-haspopup="menu"
              aria-expanded={sceneAddGeomKindSubmenuOpen}
              aria-disabled={!canAutoParentGeom}
              onClick={() => {
                if (!canAutoParentGeom) return;
                handleContextAddGeomToBody(
                  addBodyToRoot ? null : (targetBodyUuid ?? null),
                  "sphere",
                  { forceNewRoot: addBodyToRoot }
                );
              }}
              onMouseEnter={() => {
                if (!canAutoParentGeom) return;
                setSceneAddGeomKindSubmenuOpen(true);
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              onFocus={() => {
                if (!canAutoParentGeom) return;
                setSceneAddGeomKindSubmenuOpen(true);
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                canAutoParentGeom
                  ? "text-gray-200 hover:bg-gray-700/90"
                  : "text-gray-300 opacity-60 cursor-not-allowed"
              }`}
            >
              <ExplosionIcon className="w-4 h-4" />
            </button>
          </Tooltip>
          {!(addBodyToRoot && !targetBodyUuid) ? (
            <Tooltip content="Constraint">
              <button
                type="button"
                role="menuitem"
                aria-label="Constraint"
                aria-disabled={!targetBodyUuid && !sceneContextMenuData.candidates.joint?.uuid}
                onClick={() => {
                  const jointUuid =
                    sceneContextMenuData.priorityPick?.targetType === "joint"
                      ? sceneContextMenuData.priorityPick?.candidate?.uuid || null
                      : null;
                  if (!targetBodyUuid && !jointUuid) return;
                  handleContextAddConstraint(targetBodyUuid, jointUuid);
                }}
                onMouseEnter={() => {
                  setSceneAddJointKindSubmenuOpen(false);
                  setSceneAddGeomKindSubmenuOpen(false);
                  setSceneAddVisualGeomKindSubmenuOpen(false);
                }}
                onFocus={() => {
                  setSceneAddJointKindSubmenuOpen(false);
                  setSceneAddGeomKindSubmenuOpen(false);
                  setSceneAddVisualGeomKindSubmenuOpen(false);
                }}
                className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                  targetBodyUuid || sceneContextMenuData.candidates.joint?.uuid
                    ? "text-gray-200 hover:bg-gray-700/90"
                    : "text-gray-300 opacity-60 cursor-not-allowed"
                }`}
              >
                <GitMerge className="w-4 h-4" aria-hidden="true" />
              </button>
            </Tooltip>
          ) : null}
          <Tooltip content={tendonTooltip}>
            <button
              type="button"
              role="menuitem"
              aria-label="Tendon"
              aria-disabled={!tendonTargetJointUuid}
              onClick={() => {
                if (!tendonTargetJointUuid) return;
                handleContextAddTendon(tendonTargetJointUuid);
              }}
              onMouseEnter={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              onFocus={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                tendonTargetJointUuid
                  ? "text-gray-200 hover:bg-gray-700/90"
                  : "text-gray-300 opacity-60 cursor-not-allowed"
              }`}
            >
              <Spline className="w-4 h-4" aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip
            content={
              addBodyToRoot
                ? "Add camera (fixed to world)"
                : sceneContextMenuData.priorityPick?.targetType === "site"
                  ? "Mount camera to this site"
                  : sceneContextMenuData.priorityPick?.targetType === "joint"
                    ? "Mount camera to this joint's body"
                    : sceneContextMenuData.priorityPick?.targetType === "body"
                      ? "Mount camera to this body"
                      : sceneContextMenuData.priorityPick?.targetType === "object"
                        ? "Mount camera to object root"
                        : "Mount camera to body"
            }
          >
            <button
              type="button"
              role="menuitem"
              aria-label={addBodyToRoot ? "Add fixed world camera" : "Mount camera"}
              aria-disabled={
                addBodyToRoot
                  ? isPhysicsRunning
                  : !(
                      targetBodyUuid ||
                      sceneContextMenuData.priorityPick?.targetType === "site" ||
                      sceneContextMenuData.priorityPick?.targetType === "joint" ||
                      sceneContextMenuData.priorityPick?.targetType === "body" ||
                      sceneContextMenuData.priorityPick?.targetType === "object"
                    )
              }
              onClick={() => {
                if (addBodyToRoot) {
                  handleContextAddFixedWorldCamera();
                } else {
                  handleContextAddCameraFromScenePick(targetBodyUuid);
                }
              }}
              onMouseEnter={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              onFocus={() => {
                setSceneAddJointKindSubmenuOpen(false);
                setSceneAddGeomKindSubmenuOpen(false);
                setSceneAddVisualGeomKindSubmenuOpen(false);
              }}
              className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                addBodyToRoot
                  ? isPhysicsRunning
                    ? "text-gray-300 opacity-60 cursor-not-allowed"
                    : "text-gray-200 hover:bg-gray-700/90"
                  : targetBodyUuid ||
                      sceneContextMenuData.priorityPick?.targetType === "site" ||
                      sceneContextMenuData.priorityPick?.targetType === "joint" ||
                      sceneContextMenuData.priorityPick?.targetType === "body" ||
                      sceneContextMenuData.priorityPick?.targetType === "object"
                    ? "text-gray-200 hover:bg-gray-700/90"
                    : "text-gray-300 opacity-60 cursor-not-allowed"
              }`}
            >
              <Camera className="w-4 h-4" aria-hidden="true" />
            </button>
          </Tooltip>
        </div>
        {sceneAddJointKindSubmenuOpen && canAddAnyJoint && jointKindOptions.length > 0 ? (
          <div
            role="menu"
            aria-label="Joint types"
            className="rounded-md border border-gray-600 p-1 shadow-xl"
            style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
          >
            <div className="flex items-center gap-1">
              {[
                ...jointKindOptions.map((option) => ({
                  ...option,
                  Icon: jointIconByKind[option.kind] || JointIcon,
                })),
              ].map(({ kind, label, Icon, disabled, description }) => (
                <Tooltip key={kind} content={description || label}>
                  <button
                    type="button"
                    role="menuitem"
                    aria-label={label}
                    aria-disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      handleContextAddJointToBody(
                        addBodyToRoot ? null : (targetBodyUuid ?? null),
                        kind,
                        { forceNewRoot: addBodyToRoot }
                      );
                    }}
                    className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                      disabled
                        ? "text-gray-300 opacity-60 cursor-not-allowed"
                        : "text-gray-200 hover:bg-gray-700/90"
                    }`}
                  >
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        ) : null}
        {sceneAddVisualGeomKindSubmenuOpen && canAutoParentGeom ? (
          <div
            role="menu"
            aria-label="Visual geometry types"
            className="rounded-md border border-gray-600 p-1 shadow-xl"
            style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
          >
            <div className="flex items-center gap-1">
              {[
                { kind: "box", label: "Box", Icon: BoxGeomIcon },
                { kind: "sphere", label: "Sphere", Icon: SphereGeomIcon },
                { kind: "capsule", label: "Capsule", Icon: CapsuleGeomIcon },
                { kind: "cylinder", label: "Cylinder", Icon: CylinderGeomIcon },
                { kind: "mesh", label: "Mesh", Icon: Grid3X3 },
              ].map(({ kind, label, Icon }) => (
                <Tooltip key={kind} content={label}>
                  <button
                    type="button"
                    role="menuitem"
                    aria-label={label}
                    onClick={() =>
                      handleContextAddVisualGeomToBody(
                        addBodyToRoot ? null : (targetBodyUuid ?? null),
                        kind,
                        { forceNewRoot: addBodyToRoot }
                      )
                    }
                    className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                  >
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        ) : null}
        {sceneAddGeomKindSubmenuOpen && canAutoParentGeom ? (
          <div
            role="menu"
            aria-label="Collision geometry types"
            className="rounded-md border border-gray-600 p-1 shadow-xl"
            style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
          >
            <div className="flex items-center gap-1">
              {[
                { kind: "box", label: "Box", Icon: BoxGeomIcon },
                { kind: "sphere", label: "Sphere", Icon: SphereGeomIcon },
                { kind: "capsule", label: "Capsule", Icon: CapsuleGeomIcon },
                { kind: "cylinder", label: "Cylinder", Icon: CylinderGeomIcon },
              ].map(({ kind, label, Icon }) => (
                <Tooltip key={kind} content={label}>
                  <button
                    type="button"
                    role="menuitem"
                    aria-label={label}
                    onClick={() =>
                      handleContextAddGeomToBody(
                        addBodyToRoot ? null : (targetBodyUuid ?? null),
                        kind,
                        { forceNewRoot: addBodyToRoot }
                      )
                    }
                    className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                  >
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {sceneContextMenuOpen ? (
        <div
          ref={sceneContextMenuRef}
          role="menu"
          aria-label={hasContextTarget ? "Scene context menu" : "Scene canvas context menu"}
          className={`fixed z-[2000] w-fit rounded-md border p-0.5 shadow-xl select-none ${
            isPhysicsRunning ? "border-amber-400/70 shadow-amber-950/30" : "border-gray-600"
          }`}
          style={{
            left: sceneContextMenuPos.x,
            top: sceneContextMenuPos.y,
            backgroundColor: "rgba(40, 40, 40, 0.95)",
          }}
        >
          <div className="px-2 pb-1.5 pt-1.5">
            {hasContextTarget ? (
              <OrderedIconActionContextRow
                activeSubmenuKey={activeSubmenu}
                setActiveSubmenuKey={(key) => {
                  if (key) openSubmenu(key);
                  else setActiveSubmenu(null);
                }}
                onMouseLeave={closeSubmenuSoon}
                itemRole="menuitem"
                submenuRole="menu"
                submenuClassName="mt-1 rounded-md border border-gray-600 p-1 shadow-xl"
                submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                actions={[
                  {
                    key: "inspect",
                    label: "Inspect",
                    icon: <ScanSearch className="w-4 h-4" aria-hidden="true" />,
                    disabled: !sceneContextMenuData.hasHit,
                    submenu: sceneContextMenuData.hasHit ? (
                      <div className="flex items-center gap-1">
                        {[
                          {
                            type: "object",
                            label: "Entire Object",
                            Icon: EntireObjectIcon,
                            iconClass: "w-4 h-4",
                          },
                          { type: "body", label: "Body", Icon: Box, iconClass: "w-4 h-4" },
                          { type: "mesh", label: "Visuals", Icon: Grid3X3, iconClass: "w-4 h-4" },
                          {
                            type: "geom",
                            label: "Collision",
                            Icon: ExplosionIcon,
                            iconClass: "w-4 h-4",
                          },
                          {
                            type: "inertial",
                            label: "Inertial",
                            Icon: EllipsoidIcon,
                            iconClass: "w-4 h-4",
                          },
                          { type: "joint", label: "Joint", Icon: JointIcon, iconClass: "w-4 h-4" },
                          { type: "site", label: "Site", Icon: MapPin, iconClass: "w-4 h-4" },
                          { type: "camera", label: "Camera", Icon: Camera, iconClass: "w-4 h-4" },
                          { type: "actuator", label: "Actuator", Icon: Zap, iconClass: "w-4 h-4" },
                          (() => {
                            const isTendon = String(
                              sceneContextMenuData.candidates.constraint?.key ?? ""
                            ).startsWith("tendon:");
                            return {
                              type: "constraint",
                              label: isTendon ? "Tendon" : "Constraint",
                              Icon: isTendon ? Spline : GitMerge,
                              iconClass: "w-4 h-4",
                            };
                          })(),
                        ]
                          .filter(({ type }) => !!sceneContextMenuData.candidates[type])
                          .map(({ type, label, Icon, iconClass }) => {
                            const tooltipContent = (
                              <span className="whitespace-nowrap">{label}</span>
                            );
                            return (
                              <Tooltip key={type} content={tooltipContent}>
                                <button
                                  type="button"
                                  role="menuitem"
                                  aria-label={label}
                                  onClick={() => {
                                    handleContextInspectSelect(type);
                                  }}
                                  className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                                >
                                  <Icon className={iconClass} />
                                </button>
                              </Tooltip>
                            );
                          })}
                      </div>
                    ) : null,
                  },
                  {
                    key: "delete",
                    label: "Delete",
                    icon: <Trash2 className="w-4 h-4" aria-hidden="true" />,
                    disabled: isPhysicsRunning || !hasContextTarget,
                    disabledByPhysics: isPhysicsRunning,
                    onDefaultClick: () => {
                      if (isPhysicsRunning || !hasContextTarget) return;
                      const candidates = sceneContextMenuData.candidates || {};
                      const primary = sceneContextMenuData.primaryTargetType;
                      if (primary && candidates[primary]) {
                        handleContextDeleteSelect(primary);
                        return;
                      }
                      const order = [
                        "object",
                        "body",
                        "mesh",
                        "geom",
                        "inertial",
                        "joint",
                        "site",
                        "camera",
                      ];
                      const pick = order.find((t) => candidates[t]);
                      if (pick) handleContextDeleteSelect(pick);
                    },
                    submenu:
                      !isPhysicsRunning && hasContextTarget ? (
                        <div className="flex items-center gap-1">
                          {[
                            {
                              type: "object",
                              label: "Delete Entire Object",
                              Icon: EntireObjectIcon,
                              iconClass: "w-4 h-4",
                            },
                            { type: "body", label: "Delete Body", Icon: Box, iconClass: "w-4 h-4" },
                            {
                              type: "mesh",
                              label: "Delete Visual",
                              Icon: Grid3X3,
                              iconClass: "w-4 h-4",
                            },
                            {
                              type: "geom",
                              label: "Delete Collision",
                              Icon: ExplosionIcon,
                              iconClass: "w-4 h-4",
                            },
                            {
                              type: "inertial",
                              label: "Delete Inertial",
                              Icon: EllipsoidIcon,
                              iconClass: "w-4 h-4",
                            },
                            {
                              type: "joint",
                              label: "Delete Joint",
                              Icon: JointIcon,
                              iconClass: "w-4 h-4",
                            },
                            {
                              type: "site",
                              label: "Delete Site",
                              Icon: MapPin,
                              iconClass: "w-4 h-4",
                            },
                            {
                              type: "camera",
                              label: "Delete Camera",
                              Icon: Camera,
                              iconClass: "w-4 h-4",
                            },
                          ]
                            .filter(({ type }) => !!sceneContextMenuData.candidates[type])
                            .map(({ type, label, Icon, iconClass }) => {
                              const tooltipContent = (
                                <span className="whitespace-nowrap">{label}</span>
                              );
                              return (
                                <Tooltip key={type} content={tooltipContent}>
                                  <button
                                    type="button"
                                    role="menuitem"
                                    aria-label={label}
                                    onClick={() => {
                                      handleContextDeleteSelect(type);
                                    }}
                                    className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                                  >
                                    <Icon className={iconClass} />
                                  </button>
                                </Tooltip>
                              );
                            })}
                        </div>
                      ) : null,
                  },
                  {
                    key: "add",
                    label: "Add new",
                    icon: <Plus className="w-4 h-4" aria-hidden="true" />,
                    disabled: isPhysicsRunning || !contextAddTargetBodyUuid,
                    disabledByPhysics: isPhysicsRunning,
                    onDefaultClick: () => {
                      if (isPhysicsRunning || !contextAddTargetBodyUuid) return;
                      handleContextAddBody(contextAddTargetBodyUuid);
                    },
                    submenu:
                      !isPhysicsRunning && contextAddTargetBodyUuid
                        ? renderContextAddSubmenu(contextAddTargetBodyUuid, false)
                        : null,
                  },
                  {
                    key: "duplicate",
                    label: "Duplicate",
                    icon: <Copy className="w-4 h-4" aria-hidden="true" />,
                    disabled: isPhysicsRunning || !contextAddTargetBodyUuid,
                    disabledByPhysics: isPhysicsRunning,
                    onDefaultClick: () => {
                      if (isPhysicsRunning || !contextAddTargetBodyUuid) return;
                      const geomName = sceneContextMenuData.candidates?.geom?.name;
                      if (sceneContextMenuData.primaryTargetType === "geom" && geomName) {
                        handleContextDuplicateCollision(geomName);
                        return;
                      }
                      handleContextDuplicateBody(contextAddTargetBodyUuid, false);
                    },
                    submenu:
                      !isPhysicsRunning && contextAddTargetBodyUuid ? (
                        <DuplicateMenuContent
                          buttonExtraProps={{ role: "menuitem" }}
                          onDuplicateBody={() =>
                            handleContextDuplicateBody(contextAddTargetBodyUuid, false)
                          }
                          onDuplicateBranch={() =>
                            handleContextDuplicateBody(contextAddTargetBodyUuid, true)
                          }
                          onDuplicateCollision={
                            sceneContextMenuData.primaryTargetType === "geom" &&
                            sceneContextMenuData.candidates.geom?.name
                              ? () =>
                                  handleContextDuplicateCollision(
                                    sceneContextMenuData.candidates.geom.name
                                  )
                              : null
                          }
                        />
                      ) : null,
                  },
                  {
                    key: "clearScene",
                    label: "Clear scene",
                    icon: <Eraser className="w-4 h-4" aria-hidden="true" />,
                    disabled: isPhysicsRunning,
                    disabledByPhysics: isPhysicsRunning,
                    onDefaultClick: handleContextClearScene,
                    buttonClassName: "hover:!bg-red-600/90",
                  },
                ]}
              />
            ) : (
              <OrderedIconActionContextRow
                activeSubmenuKey={activeSubmenu}
                setActiveSubmenuKey={(key) => {
                  if (key) openSubmenu(key);
                  else setActiveSubmenu(null);
                }}
                onMouseLeave={closeSubmenuSoon}
                itemRole="menuitem"
                submenuRole="menu"
                submenuClassName="mt-1 rounded-md border border-gray-600 p-1 shadow-xl"
                submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                actions={[
                  {
                    key: "add",
                    label: "Add new",
                    icon: <Plus className="w-4 h-4" aria-hidden="true" />,
                    disabled: isPhysicsRunning,
                    disabledByPhysics: isPhysicsRunning,
                    onDefaultClick: () => {
                      if (isPhysicsRunning) return;
                      handleContextAddBody(null);
                    },
                    submenu: !isPhysicsRunning
                      ? renderContextAddSubmenu(worldRootTargetBodyUuid, true)
                      : null,
                  },
                  {
                    key: "clearScene",
                    label: "Clear scene",
                    icon: <Eraser className="w-4 h-4" aria-hidden="true" />,
                    disabled: isPhysicsRunning,
                    disabledByPhysics: isPhysicsRunning,
                    onDefaultClick: handleContextClearScene,
                    buttonClassName: "hover:!bg-red-600/90",
                  },
                ]}
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
